import { Component, OnInit } from '@angular/core';
import { FormBase } from '../shared/form-base.component';
import { AbstractControl, FormBuilder, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { AsmsService } from '../../services/asms.service';
import { ProvisionsService } from '../../services/provisions.service';
import { combineLatest } from 'rxjs';
import { debounceTime, filter, map, switchMap } from 'rxjs/operators';
import { AccountType } from 'src/app/enums/account-type.enum';
import { EnvironmentType } from 'src/app/enums/environment.enum';
import { environment } from '../../../environments/environment';
import { approvedOrgs } from 'src/app/shared/app-data/approved-orgs';
@Component({
  selector: 'app-form-aws',
  templateUrl: './form-aws.component.html',
  styleUrls: [
    // '../shared/form.component.scss', //TODO: use this if azure eventually shares the same css sheet
    'form-aws.component.scss',
  ]
})

export class FormAwsComponent extends FormBase implements OnInit{
  fileToUpload: File | null = null;
  fileName: string | null = null;
  awsEnvName: string | null = null;

  firstFormGroup = this._formBuilder.group({
    asms: [null as any, Validators.required],
    suffix: ['main', [
      Validators.pattern(/^[a-zA-Z0-9]*$/),
      Validators.maxLength(8),
    ]],
    region: ['us-east-2', Validators.required],
    environment: [null, Validators.required],
    screenshot: [null, Validators.required],
    orgUnit: ['Online', Validators.required],
    repos: ['', [Validators.required, this.githubReposValidator()]],
  });

  secondFormGroup = this._formBuilder.group({
    secondCtrl: ['', Validators.required],
  });

  constructor(
    private _formBuilder: FormBuilder,
    protected override asmsService: AsmsService,
    protected override router: Router,
    public override dialog: MatDialog,
    protected override provisionService: ProvisionsService,

  ) {
    super(router, dialog, provisionService, asmsService);
  }

  ngOnInit(): void {
    this.getAsmsList(this.firstFormGroup);

    // Updates the aws environment name + checks if the account name is unique when asms + environment are changed
    combineLatest([
      this.firstFormGroup.controls.asms.valueChanges,
      this.firstFormGroup.controls.environment.valueChanges
    ])
      .pipe(
        debounceTime(500), //wait for user to stop typing
        filter(([asmsId, env]) => !!asmsId && !!env),
        switchMap(([asmsId, env]) => {
          // First operation: Update the AWS environment name
          return this.getAwsEnvName(asmsId, env!).then(name => {
            this.awsEnvName = name;
          });
        }),
        switchMap(() => {
          // Second operation: Check if the generated AWS account name is unique
          const awsAccountNamePrefix = this.buildAwsAccountNamePrefix().replace(/\s+/g, '');
          const suffix = this.firstFormGroup.controls.suffix.value;
          const awsAccountName = awsAccountNamePrefix + (suffix?.toLowerCase() || 'main');
    
          return this.provisionService.getAwsNameUnique(awsAccountName);
        }),
      )
      .subscribe({
        next: (isUnique: boolean) => {
          // Handle unique name validation
          this.firstFormGroup.get('suffix')?.setErrors(isUnique ? null : { notUnique: true });
        },
        error: err => {
          console.error('Error during operation: ', err);
          this.firstFormGroup.get('suffix')?.setErrors({ apiError: true });
        },
      });
            
      // Checks if the account name is unique when asms, environment, or suffix changes
      combineLatest([
        this.firstFormGroup.controls.asms.valueChanges,
        this.firstFormGroup.controls.environment.valueChanges,
        this.firstFormGroup.controls.suffix.valueChanges,
      ])
        .pipe(
          debounceTime(300),
          filter(([asmsId, env]) => !!asmsId && !!env),
          switchMap(([asmsId, env, suffix]) => {
            const awsAccountNamePrefix = this.buildAwsAccountNamePrefix().replace(/\s+/g, '');
            const awsAccountName = awsAccountNamePrefix + (suffix?.toLowerCase() || 'main');
            return this.provisionService.getAwsNameUnique(awsAccountName);
          })
        )
        .subscribe({
          next: (isUnique : boolean) => {
            this.firstFormGroup.get('suffix')?.setErrors(isUnique ? null : { notUnique: true });
          },
          error: (err) => {
            console.error("Error checking if name is unique: ", err);
            this.firstFormGroup.get('suffix')?.setErrors({ apiError: true });
          }
        });

  }

  /**
   * Builds the aws account name prefix based on the selected asms and environment
   * @returns {string} - aws account name prefix
   */
  buildAwsAccountNamePrefix() : string{
    const asmsId = this.firstFormGroup.get('asms')?.value || 'ASMS';
    const displayEnv = this.firstFormGroup.get('environment')?.value ? this.awsEnvName : 'Env.';
    
    const asmsItem = this.asmsList.find(a => a.asmsId === asmsId);
    const asmsAcronym = asmsItem?.asmsAcronym.toLowerCase() || 'ASMS Acronym';
    
    return `aws${asmsId} - ${displayEnv} - ${asmsAcronym} - `;
  }

  /**
   * Returns the number of accounts for AsmsId in env + 1
   * @param asmsId 
   * @param {number} env - enum value of the environment
   * @returns {string} - the aws environment name
   */
  async getAwsEnvName(asmsId: string, envNum: number): Promise<string> {
    const envCharMap: Record<number, string> = {
      [EnvironmentType.Dev]: 'd',
      [EnvironmentType.Test]: 't',
      [EnvironmentType.Prod]: 'p',
    };
    const awsEnvChar = envCharMap[envNum] || '';

    let accounts = await this.provisionService.getAccountsByAsms(asmsId);
    accounts = accounts.filter(account => account.type == 0)
    const numAccounts = accounts.filter((account: any) => account.environment == envNum).length;
    return `${awsEnvChar}${numAccounts + 1}`;
  }

  /**
   * @param event - The file input change event containing the selected file.
   * Sets the `fileName` property to the name of the selected file if available.
   */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.fileName = input.files[0].name;
    }
  }

  /**
   * @returns {string | undefined} - the owner email associated with the form selected asms
   */
  getOwnerEmail(): string | undefined {
    const asmsId = this.firstFormGroup.controls['asms'].value;
    const asmsItem = this.asmsList.find(a => a.asmsId === asmsId);
    return asmsItem?.ownerEmail;
  }

  envSwitch(env: string) {
    let environment;
    switch (env) {
      case "0":
        environment = "Dev";
        break;
      case "1":
        environment = "Test";
        break;
      case "2":
        environment = "Prod";
        break;
    }
    return environment || "Dev"
  }

  /**
   * Parses data from the first form group and creates an account based on that information
   * @returns {string | undefined} - the owner email associated with the specified asms
   */
  submit() {
    const formData = this.firstFormGroup.value;
    const repos = this.getRepoList();
    const awsAccountNamePrefix = this.buildAwsAccountNamePrefix().replace(/\s+/g, '');
    const awsAccountName = awsAccountNamePrefix + (formData.suffix?.toLowerCase() || 'main');

    const defaultBudgetSize = "small";
    
    const rootEmails = {
      "dev": { prefix: "aws.devtest.rootuser+", suffix: "@pp.gm.com" },
      "prod": { prefix: "aws.rootuser+", suffix: "@gm.com" },
      default: { prefix: "aws.devtest.rootuser+", suffix: "@pp.gm.com" },
    };
    
    const { prefix, suffix  } = rootEmails[environment.envName as keyof typeof rootEmails];
    const rootEmail = prefix + awsAccountName + suffix;

    const ownerEmail = this.getOwnerEmail();


    const budgetValues = {
      "small": 500,
      "medium": 1000,
      "large": 2500,
    }

    let createAccount = {
      "accountName": awsAccountName,
      "asms": this.firstFormGroup.controls.asms.value,
      "environment": parseInt(formData.environment ?? "0"), //TODO: use enum here if needed
      "type": AccountType.AWS,
      "rootEmail": rootEmail,
      "sharedEmail": ownerEmail,
      "hasInfrastructure": false,
      "orgUnit": "Workloads/GM-" + this.envSwitch(formData.environment ?? "0") + "-Landing-Zones/" + formData.orgUnit,
      "budget": budgetValues?.[defaultBudgetSize],
      "asmsOwnerEmail": ownerEmail, //will get overwritten in the backend
      "region": formData.region,
      "forceUpdate": false,
      "bypassCreation": false,
      "githubRepos": repos
    };

    if (!environment.production) {
      console.log("CREATING ACCOUNT: ", createAccount);
    }
    this.provisionService.createAccount(createAccount).subscribe((accounts: any) => {
      if (accounts) {
        if (!environment.production) console.log("ACCOUNT CREATED: ", accounts);
        this.openAccountSummaryDialog(accounts);
      }
      return accounts;

    })

  }

  /**
   * Checks the repo input, if there are more than 10 repos, 
   * or if a value other than a github url is provided, false is 
   * returned, otherwise, true
   * @returns 
   */
  githubReposValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const repos = control.value;
      if (!repos) {
        return null; // No validation errors if the field is empty
      }

      const reposArray = repos.split(',').map((repo: string) => repo.trim());
      if (reposArray.length > 10) {
        return { tooManyRepos: true };
      }

      const githubRepoPattern = /^([a-zA-Z0-9_ -]+)\/([a-zA-Z0-9_-]+)\/?.*$/;
      for (const repo of reposArray) {
        if (!githubRepoPattern.test(repo)) {
          return { invalidRepoFormat: true };
        }

        const match = repo.match(githubRepoPattern);
        //TODO: need to add logic here to check if org name is valid
        //awaiting list of approved orgs
        if (!match) {
          console.log("invalid org format");
          return { invalidRepo: true };
        }

        const orgName = match[1].toUpperCase();
        if (!approvedOrgs.includes(orgName)) {
          console.log("invalid org");
          return { invalidOrg: true }
        }
      }

      return null; // No validation errors
    };
  }

  /**
   * Returns a list of strings of repo names
   * @returns list of strings
   */
  getRepoList() {
    return this.firstFormGroup.controls.repos.value?.split(',').map(item => item.trim());
  }


}
